import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import {
  HeartPulse, Dna, GraduationCap, Leaf, Globe2, Cpu, MapPin,
} from 'lucide-react';
import { Reveal, SectionLabel } from './Reveal';
import { PROJECT } from '../../data/content';
import renderImg from '../../assets/project-b.png';

const ECO_ICONS = {
  excellence: HeartPulse,
  biotech: Dna,
  education: GraduationCap,
  wellness: Leaf,
  tourism: Globe2,
  smart: Cpu,
};

const { intro, flagship: f } = PROJECT;

export const Project = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1.18, 1]);

  return (
    <section
      id="project"
      ref={ref}
      data-testid="project-section"
      className="relative border-t border-white/5 bg-navy-950 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>The Project — Flagship Programme</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                Global Healthcare City
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="space-y-5">
              {intro.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className="text-base leading-relaxed text-slate-300">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Conceptual render */}
        <Reveal delay={0.1}>
          <div className="mt-16 overflow-hidden border border-white/10">
            <div className="relative aspect-[16/10] overflow-hidden sm:aspect-[21/9]">
              <motion.img
                style={{ scale }}
                src={renderImg}
                alt="Global Healthcare City conceptual masterplan render"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 lg:p-8">
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-gold">
                  Conceptual masterplan
                </span>
                <p className="mt-2 font-serif text-xl text-white lg:text-2xl">
                  A self-sustaining ecosystem for health, innovation & wellness
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Flagship vision */}
        <div className="mt-8 border border-white/10 bg-navy-900 p-8 lg:p-12">
          <Reveal>
            <SectionLabel>The Vision</SectionLabel>
            <div className="mt-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <h3 className="font-serif text-3xl tracking-tight text-white lg:text-4xl">
                  {f.name}
                </h3>
                <p className="mt-2 font-serif text-lg italic text-gold">
                  {f.tagline}
                </p>
              </div>
            </div>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-300">
              {f.lead}
            </p>
          </Reveal>

          {/* Key stats */}
          <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-6">
            {f.stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="h-full bg-navy-950 p-6">
                  <div className="font-serif text-3xl leading-none text-gold">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-white/80">
                    {s.unit}
                  </div>
                  <div className="mt-3 text-xs leading-relaxed text-slate-400">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Powerful ecosystem */}
          <Reveal>
            <h4 className="mt-14 text-xs font-bold uppercase tracking-[0.24em] text-gold">
              A Powerful Ecosystem
            </h4>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {f.ecosystem.map((e, i) => {
              const Icon = ECO_ICONS[e.icon];
              return (
                <Reveal key={e.title} delay={(i % 3) * 0.07}>
                  <div className="group flex h-full gap-4 border border-white/10 bg-navy-950 p-6 transition-colors duration-500 hover:border-gold/40">
                    <Icon className="mt-0.5 h-6 w-6 shrink-0 text-gold" strokeWidth={1.5} />
                    <div>
                      <h5 className="font-serif text-lg text-white">{e.title}</h5>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">
                        {e.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Masterplan components */}
          <Reveal>
            <h4 className="mt-14 text-xs font-bold uppercase tracking-[0.24em] text-gold">
              Master-plan Components
            </h4>
            <div className="mt-6 flex flex-wrap gap-3">
              {f.components.map((c) => (
                <span
                  key={c}
                  className="flex items-center gap-2 border border-white/10 px-4 py-2 text-sm text-slate-300"
                >
                  <MapPin className="h-3.5 w-3.5 text-gold/70" strokeWidth={2} />
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Closing statement */}
        <Reveal delay={0.1}>
          <p className="mx-auto mt-16 max-w-4xl text-center font-serif text-2xl leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl text-balance">
            {f.closing}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
